import React, { useEffect, useMemo, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import isEmpty from 'lodash/isEmpty'
import join from 'lodash/join'
import map from 'lodash/map'
import toLower from 'lodash/toLower'
import uniqueId from 'lodash/uniqueId'
import upperFirst from 'lodash/upperFirst'

import {
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText
} from '@material-ui/core'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import { createNewStorage, moreVertical, storage } from '@galo-monitor/icons'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { selectCurrentProperty } from '@galo-monitor/stores/property/selectorProperty'
import { RawMaterialsStocksActions } from '@galo-monitor/stores/rawMaterialsStocks'
import colors from '@galo-monitor/styles/colors'
import fonts from '@galo-monitor/styles/fonts'
import Card from '@galo-monitor/web-components/Card'
import DataTable from '@galo-monitor/web-components/DataTable'
import Icon from '@galo-monitor/web-components/Icon'
import IconButton from '@galo-monitor/web-components/IconButton'
import CircularLoader from '@galo-monitor/web-components/Loader/CircularLoader'
import Popover from '@galo-monitor/web-components/Popover'
import Screen from '@galo-monitor/web-components/Screen'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import RawMaterialModal from '@galo-monitor/web-containers/modals/farm/RawMaterialModal'
import StockModal from '@galo-monitor/web-containers/modals/farm/StockModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

import useStyles from './styles'

const STOCK_ACTIONS = ['edit', 'delete']

const Stocks = () => {
  const classes = useStyles()
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const multipleActionsPopoverRef = useRef(null)

  const currentProperty = useSelector(selectCurrentProperty)

  const {
    data: stocks,
    mutate: mutateStocks,
    isValidating: isValidatingStocks
  } = useGaloMonitorApi('/farms/agricultures/stocks')

  const {
    data: rawMaterials,
    mutate: mutateRawMaterials,
    isValidating: isValidatingRawMaterials
  } = useGaloMonitorApi('/farms/agricultures/inputs')

  const userActions = useMemo(
    () => getActionsBySlug(routes.rawMaterialStocks.slug),
    [getActionsBySlug, routes.rawMaterialStocks.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        field: 'name'
      },
      {
        title: t('type', { howMany: 1 }),
        field: 'type'
      }
    ],
    [t]
  )

  const onAddStockClick = useCallback(
    () => {
      createDialog({
        id: 'create-stock',
        Component: StockModal,
        props: {
          onSuccess: mutateStocks
        }
      })
    },
    [createDialog, mutateStocks]
  )

  const onEditStockClick = useCallback(
    stock => {
      createDialog({
        id: 'edit-stock',
        Component: StockModal,
        props: {
          stock,
          onSuccess: mutateStocks
        }
      })
    },
    [createDialog, mutateStocks]
  )

  const onDeleteStockClick = useCallback(
    stock => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'male',
            howMany: 1,
            this: t('stock', { howMany: 1 }),
            name: stock.name
          }),
          onConfirm: () => {
            dispatch(RawMaterialsStocksActions.removeStock(
              stock,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('stock', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'male', howMany: 1 }))
                }))
                mutateStocks()
              },
              () => {
                createDialog({
                  id: 'stock-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'male',
                      action: toLower(t('remove')),
                      this: toLower(t('stock', { howMany: 1 }))
                    })  }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutateStocks, snackbar, t]
  )

  const stockActions = useMemo(() => ({
    add: onAddStockClick,
    edit: onEditStockClick,
    delete: onDeleteStockClick
  }), [onAddStockClick, onDeleteStockClick, onEditStockClick])

  const onClickStockActions = useCallback((action, stock) => {
    stockActions[action](stock)
    // eslint-disable-next-line no-unused-expressions
    multipleActionsPopoverRef?.current?.onClose()
  }, [stockActions])

  const onAddRawMaterialsClick = useCallback(
    () => {
      createDialog({
        id: 'create-raw-materials',
        Component: RawMaterialModal,
        props: {
          onSuccess: mutateRawMaterials
        }
      })
    },
    [createDialog, mutateRawMaterials]
  )

  const onEditRawMaterialsClick = useCallback(
    (_, rawMaterial) => {
      createDialog({
        id: 'edit-raw-material',
        Component: RawMaterialModal,
        props: {
          rawMaterial,
          onSuccess: mutateRawMaterials
        }
      })
    },
    [createDialog, mutateRawMaterials]
  )

  const onDeleteRawMaterialsClick = useCallback(
    (_, rawMaterial) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'male',
            howMany: 1,
            this: t('raw material', { howMany: 1 }),
            name: rawMaterial.name
          }),
          onConfirm: () => {
            dispatch(RawMaterialsStocksActions.removeRawMaterial(
              rawMaterial,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('raw material', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'male', howMany: 1 }))
                }))
                mutateRawMaterials()
              },
              () => {
                createDialog({
                  id: 'raw-materials-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'male',
                      action: toLower(t('remove')),
                      this: toLower(t('raw material', { howMany: 1 }))
                    })  }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutateRawMaterials, snackbar, t]
  )

  const crumbs = useMemo(() => [
    {
      title: t('stock', { howMany: 2  }),
      key: 'farm-stocks-screen',
      url: '/raw-materials-stocks',
      active: true
    }
  ], [t])

  useEffect(() => {
    mutateStocks()
    mutateRawMaterials()
  }, [currentProperty, mutateRawMaterials, mutateStocks])

  return (
    <Screen title={ routes.rawMaterialStocks.title } breadcrumbs={ crumbs }>
      <Grid container spacing={ 2 }>
        <Grid item md={ 3 }  xs={ 12 }>
          <Card
            title={ t('raw materials stock', { howMany: 2 }) }
            style={ {
              marginTop: 0,
              marginBottom: 0
            } }
            cardStyle={ {
              width: '100%',
              position: 'relative',
              paddingBottom: 50
            } }
            headerTitleProps={ {
              style: {
                padding: '0 0 15px 20px',
                fontSize: fonts.fontSize.S,
                fontWeight: fonts.fontWeight.bold
              }
            } }
          >
            <List className={ classes.container }>
              {isValidatingStocks ? <CircularLoader className={ classes.loader } /> : null}
              {isEmpty(stocks)
                ? (
                  <>
                    <Divider style={ { margin: 0 } } />
                    <ListItem className={ join([classes.item, classes.emptyMessage], ' ') }>
                      <ListItemText primary={ t('datagrid body empty data source message') } />
                    </ListItem>
                  </>
                ) : (
                  map(stocks, stock => (
                    <React.Fragment key={ stock.id }>
                      <Divider style={ { margin: 0 } } />
                      <ListItem className={ isValidatingStocks ? join([classes.item, classes.loadingItems], ' ') : classes.item }>
                        <ListItemIcon classes={ { root: classes.listItemIcon } }>
                          <Icon icon={ storage } color={ colors.darkGrey } />
                        </ListItemIcon>
                        <ListItemText primary={ stock.name } />
                        {
                          userActions.edit || userActions.delete ? (
                            <ListItemSecondaryAction className={ classes.actions }>
                              <Popover
                                ref={ multipleActionsPopoverRef }
                                popoverId={ uniqueId('popover-actions') }
                                Component={ props => (
                                  <IconButton tooltip={ t('action', { howMany: 2 }) } size="small" { ...props }>
                                    <Icon icon={ moreVertical } color={ colors.darkGrey } />
                                  </IconButton>
                                ) }
                              >
                                <List style={ { padding: 0 } }>
                                  {map(STOCK_ACTIONS, action => userActions[action] ? (
                                    <React.Fragment key={ uniqueId(action) }>
                                      <ListItem
                                        className={ join([classes.item, classes.actionItem], ' ') }
                                        onClick={ () => onClickStockActions(action, stock) }
                                        button
                                      >
                                        <ListItemText primary={ upperFirst(t(action === 'delete' ? 'remove' : action)) } />
                                      </ListItem>
                                      <Divider style={ { margin: 0 } } />
                                    </React.Fragment>
                                  ) : null)}
                                </List>
                              </Popover>
                            </ListItemSecondaryAction>
                          ) : null
                        }
                      </ListItem>
                    </React.Fragment>
                  )))}
              <Divider style={ { margin: 0 } } />
            </List>
            <div className={ classes.registerBtnContainer }>
              <IconButton
                className={ classes.registerBtn }
                tooltip={ t('register') }
                size="small"
                onClick={ stockActions.add }
                disabled={ !userActions.write }
              >
                <Icon icon={ createNewStorage } color={ colors.white } style={ { padding: 2 } } />
              </IconButton>
            </div>
          </Card>
        </Grid>
        <Grid item md xs={ 12 }>
          <DataTable
            loading={ isValidatingRawMaterials }
            data={ rawMaterials || [] }
            columns={ columns }
            onAddClick={ userActions.write && onAddRawMaterialsClick }
            onEditClick={ userActions.edit && onEditRawMaterialsClick }
            onDeleteClick={ userActions.delete && onDeleteRawMaterialsClick }
          />
        </Grid>
      </Grid>
    </Screen>
  )
}

export default Stocks
